import React, { useEffect, useState } from "react";
import { adminApi } from "../../utils/adminApi";
import { paths } from "../../paths";
import { getCache, setCache, isFresh, invalidate } from "./adminCache";
import {
  AdminShell,
  Panel,
  Modal,
  ResultModal,
  StatCard,
  inputCls,
  labelCls,
  primaryBtn,
  timeAgo,
  ANNOUNCEMENT_TAGS,
  TAG_CLS,
  Loading,
} from "./adminUi";

const MAX_MSG = 280;

const ManageAnnouncements = () => {
  const [items, setItems] = useState(() => getCache("announcements") || []);
  const [loading, setLoading] = useState(() => !getCache("announcements"));
  const [editing, setEditing] = useState(null); // item, "new", or null
  const [result, setResult] = useState(null); // result popup after post/edit/delete

  const fetchItems = () => {
    adminApi
      .get("/announcements")
      .then((res) => { setItems(res.data || []); setCache("announcements", res.data || []); })
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (isFresh("announcements")) { setLoading(false); return; }
    fetchItems();
  }, []);

  const handleDelete = async (item) => {
    if (!window.confirm("Delete this announcement?")) return;
    try {
      await adminApi.delete(`/announcements/${item.id}`);
      invalidate("dashboard");
      fetchItems();
      setResult({ kind: "deleted", item });
    } catch (err) {
      alert(err.response?.data?.message || "Could not delete");
    }
  };

  if (loading) return <Loading label="Loading announcements..." />;

  return (
    <AdminShell
      tab={paths.adminAnnouncements}
      title="Announcements"
      subtitle="Broadcast updates and important messages to all Campus Ambassadors."
      actions={
        <button onClick={() => setEditing("new")} className={primaryBtn}>+ Post Announcement</button>
      }
    >
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 pl-2">
        <StatCard label="Total" value={items.length} sub="announcements posted" />
        <StatCard label="Active" value={items.filter((i) => i.isActive !== false).length} sub="visible to CAs" />
        <StatCard label="Latest" value={items[0] ? timeAgo(items[0].createdAt) : "—"} sub="most recent post" />
      </div>

      <Panel title="Announcements" sub={`${items.length} ${items.length === 1 ? "update" : "updates"}`}>
        <div className="flex flex-col gap-4 max-h-[600px] overflow-y-auto">
          {items.length === 0 && (
            <p className="text-sm text-black/40 py-8 text-center">No announcements yet.</p>
          )}
          {items.map((a) => (
            <div key={a.id} className="border-b border-black/[0.05] last:border-none pb-4 last:pb-0">
              <div className="flex justify-between items-start gap-2">
                <div className="flex items-center gap-2">
                  <span className={`text-[9px] font-extrabold tracking-wider px-2 py-1 rounded-md uppercase ${TAG_CLS[a.tag] || TAG_CLS.Info}`}>
                    {a.tag || "Info"}
                  </span>
                  {a.isActive === false && (
                    <span className="text-[9px] font-extrabold tracking-wider px-2 py-1 rounded-md uppercase bg-gray-200 text-gray-500">Inactive</span>
                  )}
                </div>
                <span className="text-[11px] text-black/35 whitespace-nowrap flex-shrink-0">{timeAgo(a.createdAt)}</span>
              </div>
              <h4 className="font-bold text-[#2B1B17] leading-snug mt-1.5">{a.title}</h4>
              {a.body && <p className="text-xs text-black/50 mt-1 leading-relaxed">{a.body}</p>}
              <div className="flex items-center gap-3 mt-2">
                <button onClick={() => setEditing(a)} className="text-xs font-bold text-[#D15B28] hover:opacity-80">Edit →</button>
                <button onClick={() => handleDelete(a)} className="text-xs font-bold text-red-500 hover:opacity-80">Delete</button>
              </div>
            </div>
          ))}
        </div>
      </Panel>

      {editing && (
        <AnnouncementModal
          item={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={(res) => { setEditing(null); invalidate("dashboard"); fetchItems(); setResult(res); }}
        />
      )}

      {result?.kind === "created" && (
        <ResultModal
          accent="teal"
          title="Announcement Sent!"
          message="Your message has been broadcast to all CAs and is live on their dashboards."
          chip={`${(result.form.tag || "Info").toUpperCase()} · Sent just now`}
          primary={{ label: "View Announcements", onClick: () => setResult(null) }}
          secondary={{ label: "Post Another", onClick: () => { setResult(null); setEditing("new"); } }}
          onClose={() => setResult(null)}
        />
      )}

      {result?.kind === "updated" && (
        <ResultModal
          accent="olive"
          title="Changes Saved!"
          message="Your edits have been applied and are live on every CA dashboard."
          chip={`${(result.form.tag || "Info").toUpperCase()} · Updated just now`}
          primary={{ label: "View Announcements", onClick: () => setResult(null) }}
          secondary={{ label: "Done", onClick: () => setResult(null) }}
          onClose={() => setResult(null)}
        />
      )}

      {result?.kind === "deleted" && (
        <ResultModal
          accent="red"
          danger
          title="Announcement Removed"
          message={`"${result.item.title}" has been deleted and will no longer appear on CA dashboards.`}
          primary={{ label: "Done", onClick: () => setResult(null) }}
          onClose={() => setResult(null)}
        />
      )}
    </AdminShell>
  );
};

const AnnouncementModal = ({ item, onClose, onSaved }) => {
  const isEdit = !!item;
  const [form, setForm] = useState(
    isEdit
      ? { tag: item.tag || "Info", title: item.title || "", body: item.body || "", pinnedUntil: "" }
      : { tag: "Activation", title: "", body: "", pinnedUntil: "" }
  );
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.title) {
      alert("Announcement title is required.");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        title: form.title,
        body: form.body,
        tag: form.tag,
        pinnedUntil: form.pinnedUntil || null,
      };
      if (isEdit) await adminApi.put(`/announcements/${item.id}`, payload);
      else await adminApi.post("/announcements", payload);
      onSaved({ kind: isEdit ? "updated" : "created", form });
    } catch (err) {
      alert(err.response?.data?.message || "Could not post announcement");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal label={isEdit ? "Edit Announcement" : "Post Announcement"} title={isEdit ? form.title || "Edit announcement" : "Broadcast a message to all CAs"} onClose={onClose}>
      <div className="flex flex-col gap-4">
        <div>
          <label className={labelCls}>Tag Type</label>
          <div className="flex flex-wrap gap-2 mt-2">
            {ANNOUNCEMENT_TAGS.map((t) => {
              const active = form.tag === t;
              return (
                <button
                  key={t}
                  type="button"
                  onClick={() => set("tag", t)}
                  className={`text-xs font-extrabold tracking-wider uppercase px-3 py-1.5 rounded-full border transition ${
                    active ? `${TAG_CLS[t]} border-transparent` : "bg-transparent text-black/40 border-black/15 hover:border-black/30"
                  }`}
                >
                  {t}
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <label className={labelCls}>Announcement Title</label>
          <input value={form.title} onChange={(e) => set("title", e.target.value)} className={`${inputCls} mt-1.5`} placeholder="e.g. Submit your pending proofs now" />
        </div>

        <div>
          <label className={labelCls}>Message</label>
          <textarea
            value={form.body}
            maxLength={MAX_MSG}
            onChange={(e) => set("body", e.target.value)}
            rows={3}
            className={`${inputCls} mt-1.5 resize-none`}
            placeholder="Write your announcement here. Be clear and concise — CAs will see this on their dashboard immediately."
          />
          <p className="text-[11px] text-black/35 mt-1">{form.body.length} / {MAX_MSG} characters</p>
        </div>

        <div>
          <label className={labelCls}>Pin Until <span className="text-black/30 normal-case font-medium">(optional)</span></label>
          <div className="flex items-center gap-3 mt-1.5">
            <input type="date" value={form.pinnedUntil} onChange={(e) => set("pinnedUntil", e.target.value)} className={`${inputCls} flex-1`} />
            <span className="text-xs text-black/35">Leave blank to show indefinitely</span>
          </div>
        </div>

        {/* Preview */}
        <div className="border-t border-black/5 pt-3">
          <p className={labelCls}>Preview — how it appears on CA dashboard</p>
          <div className="bg-white rounded-xl p-4 border border-black/5 mt-2">
            <div className="flex justify-between items-start gap-2">
              <span className={`text-[9px] font-extrabold tracking-wider px-2 py-1 rounded-md uppercase ${TAG_CLS[form.tag]}`}>{form.tag}</span>
              <span className="text-[11px] text-black/35">just now</span>
            </div>
            <h4 className="font-bold text-[#2B1B17] mt-1.5">{form.title || "Announcement title"}</h4>
            {form.body && <p className="text-xs text-black/50 mt-1 leading-relaxed">{form.body}</p>}
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button onClick={save} disabled={busy} className="flex-1 bg-[#D15B28] text-white font-semibold py-3.5 rounded-full hover:bg-[#b04a1f] transition disabled:opacity-60">
            {busy ? "Saving..." : isEdit ? "Save Changes ➜" : "Post Announcement ➜"}
          </button>
          <button onClick={onClose} className="px-7 py-3.5 rounded-full bg-black/5 hover:bg-black/10 transition font-semibold text-[#2B1B17]">
            Cancel
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default ManageAnnouncements;
